import { useRef, useState } from "react";
import { FileText, GripVertical, RefreshCw, Trash2, CheckCircle2, Clock, Loader2, AlertCircle, Tags } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { CorpusDocumentRow, CorpusEmbeddingStatus } from "@/lib/workspaceCorpus";
import { CorpusTopicPicker } from "./CorpusTopicPicker";

const STATUS_PILLS: Record<CorpusEmbeddingStatus, { label: string; className: string; icon: typeof CheckCircle2 }> = {
  ready:      { label: "Ready",      className: "text-emerald-600",     icon: CheckCircle2 },
  pending:    { label: "Pending",    className: "text-muted-foreground", icon: Clock },
  processing: { label: "Embedding…", className: "text-amber-600",       icon: Loader2 },
  stale:      { label: "Stale",      className: "text-amber-600",       icon: Clock },
  error:      { label: "Error",      className: "text-destructive",     icon: AlertCircle },
};

const DOC_MIME = "application/x-excerptus-corpus-document";

type Props = {
  loading: boolean;
  visibleDocs: CorpusDocumentRow[];
  editingDocId: string | "new" | null;
  onOpen: (doc: CorpusDocumentRow) => void;
  onReembed: (docId: string) => void;
  onDelete: (doc: CorpusDocumentRow) => void;
  /** documentId → array of framework_topic_ids. Optional; chips hidden if missing/empty or codex off. */
  docTopics?: Record<string, string[]>;
  /** framework_topic_id → { label }. */
  topicMeta?: Record<string, { label: string }>;
  /** Hide chips entirely when Codex is disabled for the workspace. */
  showTopicChips?: boolean;
  /** Workspace id, required for the inline row Topic picker. */
  workspaceId?: string;
  /** Inline row Topic assignment; never writes to the document body. */
  onSetDocumentTopics?: (documentId: string, topicIds: string[]) => void;
  /**
   * Called when a document is dropped onto another document row to reorder
   * within the current folder. `targetIndex` is the position the doc should
   * occupy in the visible list (already adjusted for top/bottom edge).
   */
  onReorderDocument?: (documentId: string, targetIndex: number) => void;
};


type Zone = "top" | "bottom" | null;

export const CorpusDocumentList = ({
  loading,
  visibleDocs,
  editingDocId,
  onOpen,
  onReembed,
  onDelete,
  docTopics,
  topicMeta,
  showTopicChips,
  workspaceId,
  onSetDocumentTopics,
  onReorderDocument,
}: Props) => {
  if (loading && visibleDocs.length === 0) {
    return <div className="p-3 text-xs text-muted-foreground">Loading…</div>;
  }
  if (visibleDocs.length === 0) {
    return (
      <div className="p-3 text-xs text-muted-foreground">
        No documents yet. Click <span className="font-medium">New</span> to add one.
      </div>
    );
  }
  return (
    <ul className="space-y-0.5">
      {visibleDocs.map((doc, index) => (
        <DocumentRow
          key={doc.id}
          doc={doc}
          index={index}
          active={editingDocId === doc.id}
          onOpen={onOpen}
          onReembed={onReembed}
          onDelete={onDelete}
          topicIds={showTopicChips ? docTopics?.[doc.id] ?? [] : []}
          topicMeta={topicMeta ?? {}}
          showTopicPicker={!!showTopicChips && !!onSetDocumentTopics && !!workspaceId}
          workspaceId={workspaceId}
          onSetTopics={onSetDocumentTopics}
          onReorderDocument={onReorderDocument}
        />
      ))}
    </ul>
  );
};


const DocumentRow = ({
  doc,
  index,
  active,
  onOpen,
  onReembed,
  onDelete,
  topicIds,
  topicMeta,
  showTopicPicker,
  workspaceId,
  onSetTopics,
  onReorderDocument,
}: {
  doc: CorpusDocumentRow;
  index: number;
  active: boolean;
  onOpen: (doc: CorpusDocumentRow) => void;
  onReembed: (docId: string) => void;
  onDelete: (doc: CorpusDocumentRow) => void;
  topicIds: string[];
  topicMeta: Record<string, { label: string }>;
  showTopicPicker: boolean;
  workspaceId?: string;
  onSetTopics?: (documentId: string, topicIds: string[]) => void;
  onReorderDocument?: (documentId: string, targetIndex: number) => void;
}) => {

  const pill = STATUS_PILLS[doc.embedding_status];
  const Icon = pill.icon;
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [zone, setZone] = useState<Zone>(null);
  const [topicPickerOpen, setTopicPickerOpen] = useState(false);

  const onDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(DOC_MIME, JSON.stringify({ id: doc.id }));
    e.dataTransfer.effectAllowed = "move";
  };

  const computeZone = (e: React.DragEvent): Zone => {
    const el = rowRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return e.clientY - r.top < r.height / 2 ? "top" : "bottom";
  };

  const onDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(DOC_MIME)) return;
    if (!onReorderDocument) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setZone(computeZone(e));
  };

  const onDrop = (e: React.DragEvent) => {
    const payload = e.dataTransfer.getData(DOC_MIME);
    setZone(null);
    if (!payload || !onReorderDocument) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      const { id } = JSON.parse(payload) as { id: string };
      if (!id || id === doc.id) return;
      const z = computeZone(e);
      const target = z === "top" ? index : index + 1;
      onReorderDocument(id, target);
    } catch { /* noop */ }
  };

  return (
    <li>
      <div
        ref={rowRef}
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragLeave={() => setZone(null)}
        onDrop={onDrop}
        className={cn(
          "group relative flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm",
          active
            ? "border-primary bg-transparent text-foreground"
            : "border-transparent hover:bg-muted/60"
        )}
      >
        {zone === "top" && (
          <span aria-hidden className="pointer-events-none absolute inset-x-1 top-0 h-0.5 rounded bg-primary" />
        )}
        {zone === "bottom" && (
          <span aria-hidden className="pointer-events-none absolute inset-x-1 bottom-0 h-0.5 rounded bg-primary" />
        )}
        <GripVertical className="h-3 w-3 shrink-0 cursor-grab text-muted-foreground opacity-0 group-hover:opacity-100" aria-hidden />
        <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <button
          type="button"
          onClick={() => onOpen(doc)}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          <span className="truncate font-medium">{doc.title}</span>
          {topicIds.length > 0 && (
            <span className="hidden min-w-0 shrink items-center gap-1 md:inline-flex">
              {topicIds.slice(0, 3).map((tid) => {
                const label = topicMeta[tid]?.label ?? "topic";
                return (
                  <span
                    key={tid}
                    className="truncate rounded-full border border-border bg-muted/50 px-1.5 py-0 text-[10px] leading-4 text-muted-foreground"
                    title={label}
                  >
                    {label}
                  </span>
                );
              })}
              {topicIds.length > 3 && (
                <span
                  className="rounded-full border border-border bg-muted/50 px-1.5 py-0 text-[10px] leading-4 text-muted-foreground"
                  title={topicIds.slice(3).map((tid) => topicMeta[tid]?.label ?? "topic").join(", ")}
                >
                  +{topicIds.length - 3}
                </span>
              )}
            </span>
          )}
        </button>

        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn("inline-flex shrink-0 items-center gap-1 text-[11px]", pill.className)}>
              <Icon className={cn("h-3 w-3", doc.embedding_status === "processing" && "animate-spin")} />
              {pill.label}
              {doc.chunk_count > 0 && doc.embedding_status === "ready" && (
                <span className="text-muted-foreground">· {doc.chunk_count}</span>
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {doc.embedding_status === "error"
              ? doc.last_embedding_error ?? "Embedding failed"
              : doc.embedding_status === "stale"
                ? "Document changed since last embedding. Re-embed to refresh AI grounding."
                : doc.embedding_status === "ready"
                  ? `Embedded · ${doc.chunk_count} chunk${doc.chunk_count === 1 ? "" : "s"}`
                  : pill.label}
          </TooltipContent>
        </Tooltip>
        {(doc.embedding_status === "stale" || doc.embedding_status === "error") && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                aria-label="Re-embed document"
                onClick={() => onReembed(doc.id)}
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Re-embed now</TooltipContent>
          </Tooltip>
        )}
        {showTopicPicker && workspaceId && onSetTopics && (
          <Popover open={topicPickerOpen} onOpenChange={setTopicPickerOpen}>
            <Tooltip>
              <PopoverTrigger asChild>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-6 w-6",
                      topicIds.length > 0 ? "text-primary" : "opacity-0 group-hover:opacity-100"
                    )}
                    aria-label="Edit topics"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tags className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
              </PopoverTrigger>
              <TooltipContent>Edit topics</TooltipContent>
            </Tooltip>
            <PopoverContent
              className="w-72 p-2"
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              <CorpusTopicPicker
                workspaceId={workspaceId}
                value={topicIds}
                onChange={(next) => onSetTopics(doc.id, next)}
                compact
              />
            </PopoverContent>
          </Popover>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100"
          aria-label="Delete document"
          onClick={() => onDelete(doc)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </li>
  );
};
